import Avo from 'analytics/Avo';
import type { Preferences } from 'api/types/preferences.types';
import Button from 'components/atoms/Button';
import type { BottomSheetProps } from 'components/molecules/BottomSheet';
import BottomSheet from 'components/molecules/BottomSheet';
import { BUTTON_NAMES } from 'constant/button-names';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import Col from 'react-bootstrap/Col';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import PreferenceContent from './components/PreferenceContent';
import OrderInstructionForm from './OrderInstructionForm';
import type { IPreferencesOptions } from './OrderInstructionsOptions';

const StyledFooterRow = styled.div`
  position: sticky;
  bottom: 0;
  padding: 16px;
  padding-bottom: 20px;
  background-color: ${({ theme }) => theme.colors.white};
`;

const StyledClearAllButton = styled(Button)`
  width: 100px;
  border-radius: 8px;
  height: 38px;
  padding: 6px 13px;
  color: ${({ theme }) => theme.colors.crimson};
  background-color: ${({ theme }) => theme.colors.pink100};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  margin-bottom: 15px;
  margin-left: 16px;
`;

const StyledBottomSheet = styled(BottomSheet)`
  & .mb-6 {
    margin-bottom: 10px !important;
  }
`;

export interface OptionsFormProps extends BottomSheetProps {
  onClose?(): void;
  show?: boolean;
  title?: string;
  optionType?: string;
  preference?: Preferences.Preference;
  onFormSubmit?(form: string[]): any;
  hideCategoryName?: boolean;
  description?: string;
  hideSeparator?: boolean;
  hasContent?: boolean;
  preferenceDescription?: string;
  checkboxType?: 'checkbox' | 'badge';
  allowClearAllSelections?: boolean;
  alwaysHighlightOptionTitle?: boolean;
  actionText?: string;
}

const OptionsFormSheet: React.FC<OptionsFormProps> = ({
  optionType,
  preference,
  hideCategoryName = false,
  onFormSubmit = () => {},
  description,
  hasContent = false,
  preferenceDescription,
  checkboxType,
  allowClearAllSelections,
  alwaysHighlightOptionTitle,
  actionText,
  ...props
}) => {
  const formRef = React.createRef<HTMLFormElement>();
  const { t } = useTranslation();
  const theme = useTheme();
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  useEffect(() => {
    const updatePreselections = () => {
      const preSelectedValues = preference?.categories.map((category) =>
        category.options.filter((option) => Boolean(option.value)).map((option) => option.key)
      );

      const flatSelections = preSelectedValues ? preSelectedValues?.reduce((accumulator, current) => accumulator.concat(current), []) : [];

      setSelectedOptions(flatSelections);
    };

    updatePreselections();
  }, [preference?.categories]);

  const handleSubmit = () => {
    if (!formRef.current) return;
    const { elements } = formRef.current;
    const options = Array.from(elements)
      .filter(
        (el) =>
          // @ts-ignore
          el.checked
      )
      .reduce((accumulator, el) => {
        const key = el.getAttribute('value') || '';
        return [...accumulator, key];
      }, [] as string[]);

    onFormSubmit(options);
  };

  const updateOptions = useCallback(() => {
    if (!formRef.current) return;
    const { elements } = formRef.current;
    const options = Array.from(elements)
      .filter(
        (el) =>
          // @ts-ignore
          el.checked
      )
      .reduce((accumulator, el) => {
        const key = el.getAttribute('value') || '';
        return [...accumulator, key];
      }, [] as string[]);
    setSelectedOptions(options);
  }, [formRef]);

  const onChangeValue = useCallback(
    (type: string) => {
      updateOptions();
      Avo.orderInstructionsAdded({ instructionType: type });
    },
    [updateOptions]
  );

  const OptionsDescription = useMemo(
    () =>
      description && (
        <Col xs={13} className="pb-1 ml-2">
          <h4 className="font-weight-light mb-0 text-gray-dark">{description}</h4>
        </Col>
      ),
    [description]
  );

  const onClearAllSelections = useCallback(() => {
    if (!formRef.current) return;
    const { elements } = formRef.current;
    Array.from(elements).forEach((el) => {
      // @ts-ignore
      // eslint-disable-next-line no-param-reassign
      el.checked = false;
    });
    updateOptions();
  }, [formRef, updateOptions]);

  if (!preference) return null;

  return (
    <StyledBottomSheet
      className={allowClearAllSelections ? 'overflow-hidden' : ''}
      maxHeight="95%"
      hasNoSpacing
      onSubmit={handleSubmit}
      {...props}
      showDone
    >
      {hasContent && <PreferenceContent preferenceType={optionType!! as keyof IPreferencesOptions} />}
      {allowClearAllSelections && (
        <StyledClearAllButton
          backgroundColor={theme.colors.pink100}
          block
          size="lg"
          onClick={onClearAllSelections}
          disabled={selectedOptions.length === 0}
          buttonName={BUTTON_NAMES.CLEAR_ALL}
        >
          {t('common.clearAll')}
        </StyledClearAllButton>
      )}
      <Container>
        {!hasContent && OptionsDescription}
        <OrderInstructionForm
          formRef={formRef}
          checkboxType={checkboxType}
          hideCategoryName={hideCategoryName}
          preferenceDescription={preferenceDescription}
          optionType={optionType}
          selectedOptions={selectedOptions}
          preference={preference}
          onChangeValue={onChangeValue}
          alwaysHighlightOptionTitle={alwaysHighlightOptionTitle}
        />
      </Container>
      <StyledFooterRow>
        <Button
          buttonName={BUTTON_NAMES.DONE}
          textColor={theme.components.pages.pickupDropoff?.button?.textColor}
          backgroundColor={theme.components.pages.pickupDropoff?.button?.background}
          block
          size="lg"
          onClick={handleSubmit}
        >
          {actionText ?? t('common.done')}
        </Button>
      </StyledFooterRow>
    </StyledBottomSheet>
  );
};

export default OptionsFormSheet;
